// The People page's Day menu (Seán, 2026-11-03): the event's days from the year's program, in the
// time zone chosen (the Program page's own, useTimeZoneSelected.js; each conference day kept
// whole, as the Program groups it: useProgram.js), and who is on each: everyone its sessions name
// (speakers and session chairs), and the attendees of an online event on its days. The people
// sheet's Date column, which the menu read before, is empty from 2026.
import {ref, watch} from "vue";
import useProgram, {getDefaultTimeZone} from "@/composables/useProgram";
import useTimeZoneSelected from "@/composables/useTimeZoneSelected";
import useUserSafe from "@/composables/useUserSafe";

const ONLINE_KINDS = {masterclass: /Online Masterclass/i, workshop: /Online Workshop/i};

// "Fernanda Viegas; Martin Wattenberg (TBC)" → ["fernanda viegas", "martin wattenberg"]
export function getNameKeys(text) {
  return String(text || "").split(/\s*(?:;|&)\s*/)
    .map((name) => name.replace(/\s*\([^)]*\)\s*$/, "").trim().toLowerCase())
    .filter(Boolean);
}

// the online events the day belongs to: its title or a session's
function getKinds(day) {
  const titles = [day.title, ...day.events.map((event) => event.title)].join(" ");
  return Object.keys(ONLINE_KINDS).filter((kind) => ONLINE_KINDS[kind].test(titles));
}

// the event a day belongs to, for the menu's groups (Seán, 2026-11-03): the online events, the
// virtual ones, the in-person Masterclass, the in-person Workshop (its topic sessions)
function getGroup(day) {
  const titles = [day.title, ...day.events.map((event) => event.title)].join(" ");
  if (/Online/i.test(titles)) return "online";
  if (/Virtual/i.test(titles)) return "virtual";
  return /Masterclass/i.test(day.title || "") ? "masterclass" : "workshop";
}

// {key: "Apr 14", date: "Tuesday, April 14, 2026", …} → "Tue Apr 14"
const getLabel = (key, day) => `${String(day.date).slice(0, 3)} ${key}`;

function getDay(key, day) {
  const names = new Set(day.events.flatMap((event) => getNameKeys(event.name)));
  return {label: getLabel(key, day), names, kinds: getKinds(day), group: getGroup(day)};
}

// [{label, names, kinds, group}] in the program's order
export async function fetchPeopleDays(year, timeZone) {
  const program = await useProgram({timeZoneSelected: timeZone, year: String(year)});
  return Object.entries(program || {}).map(([key, day]) => getDay(key, day));
}

export default function usePeopleDays(getYear) {
  const days = ref([]);
  const timeZone = useTimeZoneSelected();
  const {user} = useUserSafe();
  const load = async () => {
    if (!timeZone.value) timeZone.value = await getDefaultTimeZone(getYear(), user.value);
    days.value = await fetchPeopleDays(getYear(), timeZone.value);
  };
  watch([timeZone, getYear], load, {immediate: true});
  return days;
}
